import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { StyledTextField } from 'components/Input/styles';
import NavigationBlocker from 'components/NavigationBlocker';

import FolderTreePicker from 'containers/Documents/components/FolderTreePicker';
import { useStore } from 'utils/hooks/store';

import { FolderFormModalProps, FolderFormValues } from './types';
import {
  StyledBoldPath,
  StyledModalContent,
  StyledNewPath,
  StyledRequiredMark,
  StyledTitleText,
} from './FolderFormModalStyles';

const MAX_FOLDER_NAME_LENGTH = 50;

const validationSchema = Yup.object({
  name: Yup.string().trim().required().max(MAX_FOLDER_NAME_LENGTH),
  parentId: Yup.string().nullable().defined(),
});

const FolderFormModal: React.FC<FolderFormModalProps> = ({
  open,
  title,
  submitButtonText,
  initialName = '',
  initialParent = null,
  excludedFolderIds = new Set(),
  folders,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { folders: foldersStore } = useStore();

  const initialValues: FolderFormValues = { name: initialName, parentId: initialParent || null };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        onSubmit(values.name.trim(), values.parentId);
        resetForm();
        onClose();
      }}
    >
      {({ values, dirty, isValid, setFieldValue, submitForm, resetForm }) => {
        const handleClose = (): void => {
          if (!loading) {
            resetForm();
            onClose();
          }
        };

        return (
          <>
            <NavigationBlocker
              when={open && dirty}
              shouldBlockNavigation={() => dirty}
              navigateMessage="You have unsaved changes. Are you sure you want to leave?"
            />
            <CustomModal
              openModal={open}
              onClose={handleClose}
              modalTitle={title}
              modalWidth="sm"
              cancelButton={
                <Button disabled={loading} onClick={handleClose} variant="outlined" size="large">
                  Cancel
                </Button>
              }
              submitButton={
                <Button
                  onClick={submitForm}
                  disabled={!values.name.trim() || !isValid || loading}
                  variant="contained"
                  size="large"
                >
                  {submitButtonText}
                </Button>
              }
            >
              <StyledModalContent>
                <div>
                  <StyledTitleText>
                    Folder name <StyledRequiredMark>*</StyledRequiredMark>
                  </StyledTitleText>
                  <StyledTextField
                    fullWidth
                    placeholder="Folder name"
                    value={values.name}
                    onChange={(e) =>
                      e.target.value.length <= MAX_FOLDER_NAME_LENGTH && setFieldValue('name', e.target.value)
                    }
                    inputProps={{ maxLength: MAX_FOLDER_NAME_LENGTH }}
                  />
                </div>
                <div>
                  <StyledTitleText>Parent folder</StyledTitleText>
                  <FolderTreePicker
                    folders={folders}
                    selectedFolderId={values.parentId}
                    onSelect={(id) => setFieldValue('parentId', id)}
                    excludedFolderIds={excludedFolderIds}
                    allowNone
                  />
                </div>
                {values.parentId && (
                  <StyledNewPath>
                    New folder path:{' '}
                    <StyledBoldPath>{`${foldersStore.getFolderPath(values.parentId)}/${values.name}`}</StyledBoldPath>
                  </StyledNewPath>
                )}
              </StyledModalContent>
            </CustomModal>
          </>
        );
      }}
    </Formik>
  );
};

export default FolderFormModal;
